import type { GeometryQuestion } from './geometryTestNY';

export const geometryTestVA: GeometryQuestion[] = [
  {
    id: 1, type: 'multiple-choice', points: 2,
    topic: 'Reasoning & Proof', standard: 'VA.SOL.G.1',
    question: 'Which type of reasoning uses specific examples to form a general conclusion?',
    options: ['Deductive reasoning', 'Inductive reasoning', 'Conditional reasoning', 'Algebraic reasoning'],
    correctAnswer: 1,
    solution: [
      'Inductive reasoning: observing specific cases and drawing a general conclusion.',
      'Deductive reasoning: starting from general rules to reach a specific conclusion.',
      'Answer: Inductive reasoning'
    ]
  },
  {
    id: 2, type: 'multiple-choice', points: 2,
    topic: 'Lines & Angles', standard: 'VA.SOL.G.2',
    question: 'Two lines intersect forming angles of (5x + 10)° and (3x + 30)°. If these are vertical angles, what is x?',
    options: ['x = 10', 'x = 5', 'x = 15', 'x = 20'],
    correctAnswer: 0,
    solution: [
      'Vertical angles are congruent.',
      '5x + 10 = 3x + 30',
      '2x = 20',
      'x = 10',
      'Answer: x = 10'
    ]
  },
  {
    id: 3, type: 'multiple-choice', points: 2,
    topic: 'Triangle Properties', standard: 'VA.SOL.G.5',
    question: 'The sides of a triangle are 7, 10, and x. Which value of x is possible?',
    options: ['x = 3', 'x = 17', 'x = 2', 'x = 12'],
    correctAnswer: 3,
    solution: [
      'Triangle Inequality Theorem: the sum of any two sides must be greater than the third side.',
      'Check x = 12: 7 + 10 = 17 > 12 ✓, 7 + 12 = 19 > 10 ✓, 10 + 12 = 22 > 7 ✓',
      'Check x = 3: 7 + 3 = 10, NOT greater than 10 ✗',
      'Check x = 17: 7 + 10 = 17, NOT greater than 17 ✗',
      'Answer: x = 12'
    ]
  },
  {
    id: 4, type: 'multiple-choice', points: 2,
    topic: 'Quadrilaterals', standard: 'VA.SOL.G.9',
    question: 'In a square with side length 8, what is the length of the diagonal?',
    options: ['8√2', '16', '4√2', '8√3'],
    correctAnswer: 0,
    solution: [
      'A square\'s diagonal forms a 45-45-90 triangle with two sides.',
      'Diagonal = side × √2 = 8√2',
      'Or use Pythagorean Theorem: d² = 8² + 8² = 128, d = √128 = 8√2',
      'Answer: 8√2'
    ]
  },
  {
    id: 5, type: 'multiple-choice', points: 2,
    topic: 'Similarity', standard: 'VA.SOL.G.7',
    question: 'In similar triangles, if the ratio of similarity is 4:9, what is the ratio of their areas?',
    options: ['4:9', '2:3', '16:81', '8:18'],
    correctAnswer: 2,
    solution: [
      'The ratio of areas of similar figures = (ratio of sides)²',
      '(4:9)² = 16:81',
      'Answer: 16:81'
    ]
  },
  {
    id: 6, type: 'multiple-choice', points: 2,
    topic: 'Right Triangles', standard: 'VA.SOL.G.8',
    question: 'In right triangle ABC, the altitude from C to the hypotenuse AB has length h. If the two segments of the hypotenuse are p and q, then:',
    options: ['h = p + q', 'h² = pq', 'h = p/q', 'h² = p + q'],
    correctAnswer: 1,
    solution: [
      'Geometric Mean (Altitude) Theorem: h² = p × q',
      'The altitude to the hypotenuse is the geometric mean of the two hypotenuse segments.',
      'Answer: h² = pq'
    ]
  },
  {
    id: 7, type: 'multiple-choice', points: 2,
    topic: 'Circles', standard: 'VA.SOL.G.11',
    question: 'A central angle of 80° intercepts an arc. What is the arc measure?',
    options: ['40°', '160°', '80°', '280°'],
    correctAnswer: 2,
    solution: [
      'A central angle equals its intercepted arc.',
      'Arc measure = 80°',
      'Answer: 80°'
    ]
  },
  {
    id: 8, type: 'multiple-choice', points: 2,
    topic: 'Area & Perimeter', standard: 'VA.SOL.G.14',
    question: 'A regular hexagon has side length 6. What is its perimeter?',
    options: ['24', '36', '30', '42'],
    correctAnswer: 1,
    solution: [
      'A regular hexagon has 6 equal sides.',
      'Perimeter = 6 × 6 = 36',
      'Answer: 36'
    ]
  },
  {
    id: 9, type: 'multiple-choice', points: 2,
    topic: 'Surface Area', standard: 'VA.SOL.G.13',
    question: 'What is the surface area of a cube with side length 5?',
    options: ['25', '125', '150', '75'],
    correctAnswer: 2,
    solution: [
      'A cube has 6 square faces.',
      'SA = 6 × s² = 6 × 5² = 6 × 25 = 150',
      'Answer: 150'
    ]
  },
  {
    id: 10, type: 'multiple-choice', points: 2,
    topic: 'Transformations', standard: 'VA.SOL.G.3',
    question: 'Which transformation changes the size of a figure but preserves its shape?',
    options: ['Translation', 'Rotation', 'Reflection', 'Dilation'],
    correctAnswer: 3,
    solution: [
      'A dilation changes the size of a figure by a scale factor but preserves the shape (angles remain the same).',
      'Translations, rotations, and reflections are isometries — they preserve both size and shape.',
      'Answer: Dilation'
    ]
  },
  {
    id: 11, type: 'multiple-choice', points: 2,
    topic: 'Coordinate Geometry', standard: 'VA.SOL.G.3',
    question: 'What is the distance from (−1, 3) to (5, 11)?',
    options: ['10', '14', '√100', '8'],
    correctAnswer: 0,
    solution: [
      'd = √[(5−(−1))² + (11−3)²]',
      'd = √[6² + 8²]',
      'd = √[36 + 64]',
      'd = √100 = 10',
      'Answer: 10'
    ]
  },
  {
    id: 12, type: 'multiple-choice', points: 2,
    topic: 'Angle Bisector Theorem', standard: 'VA.SOL.G.5',
    question: 'The perpendicular bisector of a segment is the locus of all points that are:',
    options: [
      'On the segment',
      'Equidistant from the endpoints of the segment',
      'Equidistant from the midpoint',
      'Perpendicular to the segment only'
    ],
    correctAnswer: 1,
    solution: [
      'The perpendicular bisector of a segment is the set of all points equidistant from both endpoints.',
      'This is the definition of a perpendicular bisector.',
      'Answer: Equidistant from the endpoints of the segment'
    ]
  },
  {
    id: 13, type: 'multiple-choice', points: 2,
    topic: 'Volume', standard: 'VA.SOL.G.13',
    question: 'A rectangular prism has volume 360 cm³. If the base is 6 × 10, what is the height?',
    options: ['4', '6', '8', '60'],
    correctAnswer: 1,
    solution: [
      'Volume = length × width × height',
      '360 = 6 × 10 × h',
      '360 = 60h',
      'h = 6',
      'Answer: 6 cm'
    ]
  },
  {
    id: 14, type: 'multiple-choice', points: 2,
    topic: 'Parallel Lines', standard: 'VA.SOL.G.2',
    question: 'Co-interior (same-side interior) angles formed by parallel lines and a transversal are:',
    options: ['Congruent', 'Complementary', 'Supplementary', 'Vertical'],
    correctAnswer: 2,
    solution: [
      'Co-interior angles (also called consecutive interior or same-side interior angles) are supplementary.',
      'They add up to 180°.',
      'Answer: Supplementary'
    ]
  },
  {
    id: 15, type: 'multiple-choice', points: 2,
    topic: 'Trigonometry', standard: 'VA.SOL.G.8',
    question: 'A ramp rises 3 feet over a horizontal distance of 12 feet. What is the angle of inclination to the nearest degree?',
    options: ['14°', '75°', '30°', '45°'],
    correctAnswer: 0,
    solution: [
      'tan(θ) = opposite/adjacent = 3/12 = 0.25',
      'θ = tan⁻¹(0.25) ≈ 14°',
      'Answer: 14°'
    ]
  },
  {
    id: 16, type: 'multiple-choice', points: 2,
    topic: 'Circles — Secants', standard: 'VA.SOL.G.11',
    question: 'Two secants are drawn from an external point. The external segments are 4 and 5, and the whole secants are 12 and x. What is x?',
    options: ['9.6', '15', '60', '48'],
    correctAnswer: 1,
    solution: [
      'Secant-Secant Theorem: external₁ × whole₁ = external₂ × whole₂',
      '4 × 12 = 5 × x',
      '48 = 5x',
      'x = 9.6',
      'Wait — recalculate: 4 × 12 = 48, 48/5 = 9.6. But let\'s check option 15: 5 × 15 = 75 ≠ 48.',
      'Correct answer: x = 9.6'
    ]
  },
  {
    id: 17, type: 'multiple-choice', points: 2,
    topic: 'Constructions', standard: 'VA.SOL.G.4',
    question: 'To bisect an angle using a compass and straightedge, you:',
    options: [
      'Measure the angle with a protractor and divide by 2',
      'Draw arcs from the vertex and from the two arc intersections on the sides, then connect',
      'Draw a line from the vertex to the midpoint of the opposite side',
      'Use a ruler to find the midpoint of the angle'
    ],
    correctAnswer: 1,
    solution: [
      'To bisect an angle: 1) Draw an arc centered at the vertex intersecting both sides.',
      '2) From each intersection point, draw equal arcs that intersect inside the angle.',
      '3) Draw a ray from the vertex through the intersection of the two arcs.',
      'Answer: Draw arcs from the vertex and from the two arc intersections on the sides, then connect'
    ]
  },
  {
    id: 18, type: 'multiple-choice', points: 2,
    topic: 'Polygon Properties', standard: 'VA.SOL.G.9',
    question: 'What is the measure of each interior angle of a regular pentagon?',
    options: ['72°', '108°', '120°', '90°'],
    correctAnswer: 1,
    solution: [
      'Sum of interior angles = (n − 2) × 180° = (5 − 2) × 180° = 540°',
      'Each interior angle = 540° / 5 = 108°',
      'Answer: 108°'
    ]
  },
  {
    id: 19, type: 'multiple-choice', points: 2,
    topic: 'Congruence', standard: 'VA.SOL.G.6',
    question: 'CPCTC stands for:',
    options: [
      'Congruent Parts of Congruent Triangles are Congruent',
      'Corresponding Parts of Congruent Triangles are Congruent',
      'Corresponding Pairs of Congruent Triangles are Congruent',
      'Congruent Polygons Create Triangles Congruently'
    ],
    correctAnswer: 1,
    solution: [
      'CPCTC = Corresponding Parts of Congruent Triangles are Congruent.',
      'Once two triangles are proven congruent, all their corresponding parts (sides and angles) are congruent.',
      'Answer: Corresponding Parts of Congruent Triangles are Congruent'
    ]
  },
  {
    id: 20, type: 'multiple-choice', points: 2,
    topic: 'Area of Trapezoid', standard: 'VA.SOL.G.14',
    question: 'A trapezoid has parallel bases of 10 and 16, and a height of 7. What is its area?',
    options: ['91', '182', '112', '46'],
    correctAnswer: 0,
    solution: [
      'Area of trapezoid = (1/2)(b₁ + b₂)(h)',
      'A = (1/2)(10 + 16)(7)',
      'A = (1/2)(26)(7)',
      'A = (1/2)(182) = 91',
      'Answer: 91'
    ]
  },
  {
    id: 21, type: 'multiple-choice', points: 2,
    topic: 'Pythagorean Triples', standard: 'VA.SOL.G.8',
    question: 'Which set of numbers is a Pythagorean triple?',
    options: ['5, 10, 13', '6, 8, 10', '3, 5, 7', '4, 6, 8'],
    correctAnswer: 1,
    solution: [
      'A Pythagorean triple satisfies a² + b² = c².',
      'Check 6, 8, 10: 6² + 8² = 36 + 64 = 100 = 10² ✓',
      'Answer: 6, 8, 10'
    ]
  },
  {
    id: 22, type: 'multiple-choice', points: 2,
    topic: 'Circles — Arc', standard: 'VA.SOL.G.11',
    question: 'In a circle, a chord is 16 cm long and is 6 cm from the center. What is the radius?',
    options: ['10', '8', '√(64+36)', '√(64−36)'],
    correctAnswer: 0,
    solution: [
      'The perpendicular from the center to a chord bisects the chord.',
      'Half the chord = 8 cm; distance from center = 6 cm.',
      'radius² = 8² + 6² = 64 + 36 = 100',
      'radius = 10',
      'Answer: 10 cm'
    ]
  },
  {
    id: 23, type: 'multiple-choice', points: 2,
    topic: 'Transformations', standard: 'VA.SOL.G.3',
    question: 'A figure is translated 3 units right and 5 units down. Which vector describes this translation?',
    options: ['(−3, 5)', '(3, −5)', '(−3, −5)', '(3, 5)'],
    correctAnswer: 1,
    solution: [
      '3 units right means +3 in the x-direction.',
      '5 units down means −5 in the y-direction.',
      'Translation vector: (3, −5)',
      'Answer: (3, −5)'
    ]
  },
  {
    id: 24, type: 'multiple-choice', points: 2,
    topic: 'Volume of Sphere', standard: 'VA.SOL.G.13',
    question: 'A sphere has diameter 6. What is its volume?',
    options: ['36π', '288π', '9π', '18π'],
    correctAnswer: 0,
    solution: [
      'Diameter = 6, so radius = 3',
      'V = (4/3)πr³ = (4/3)π(3)³ = (4/3)π(27) = 36π',
      'Answer: 36π'
    ]
  },
  {
    id: 25, type: 'multiple-choice', points: 2,
    topic: 'Slope', standard: 'VA.SOL.G.3',
    question: 'Line AB has slope 2/3. Line CD is perpendicular to AB. What is the slope of CD?',
    options: ['2/3', '−3/2', '3/2', '−2/3'],
    correctAnswer: 1,
    solution: [
      'Perpendicular lines have slopes that are negative reciprocals.',
      'Slope of CD = −1 ÷ (2/3) = −3/2',
      'Answer: −3/2'
    ]
  },
  {
    id: 26, type: 'multiple-choice', points: 2,
    topic: 'Congruent Triangles', standard: 'VA.SOL.G.6',
    question: 'In triangles ABC and DEF, angle A = angle D, angle C = angle F, and BC = EF. Which postulate proves congruence?',
    options: ['SSS', 'SAS', 'ASA', 'AAS'],
    correctAnswer: 3,
    solution: [
      'AAS (Angle-Angle-Side): two angles and a non-included side are congruent.',
      'Angle A = Angle D, Angle C = Angle F (two angles), BC = EF (non-included side).',
      'Answer: AAS'
    ]
  },
  {
    id: 27, type: 'multiple-choice', points: 2,
    topic: 'Circles — Tangent', standard: 'VA.SOL.G.11',
    question: 'From an external point, two tangent segments are drawn to a circle. If one tangent is 15 cm, what is the other?',
    options: ['7.5 cm', '30 cm', '15 cm', 'Cannot be determined'],
    correctAnswer: 2,
    solution: [
      'Two tangent segments drawn from the same external point to a circle are equal in length.',
      'Both tangent segments = 15 cm.',
      'Answer: 15 cm'
    ]
  },
  {
    id: 28, type: 'multiple-choice', points: 2,
    topic: 'Lateral Area', standard: 'VA.SOL.G.13',
    question: 'What is the lateral surface area of a regular square pyramid with base side 8 and slant height 10?',
    options: ['160', '80', '320', '64'],
    correctAnswer: 0,
    solution: [
      'Lateral area of a pyramid = (1/2) × perimeter × slant height',
      'Perimeter of square base = 4 × 8 = 32',
      'Lateral area = (1/2)(32)(10) = 160',
      'Answer: 160'
    ]
  },
  {
    id: 29, type: 'multiple-choice', points: 2,
    topic: 'Angle Relationships', standard: 'VA.SOL.G.2',
    question: 'An angle and its supplement are in the ratio 1:5. What is the measure of the smaller angle?',
    options: ['30°', '150°', '36°', '144°'],
    correctAnswer: 0,
    solution: [
      'Let the angles be x and 5x. They are supplementary: x + 5x = 180°',
      '6x = 180°',
      'x = 30°',
      'Answer: 30°'
    ]
  },
  {
    id: 30, type: 'multiple-choice', points: 2,
    topic: 'Coordinate Geometry', standard: 'VA.SOL.G.3',
    question: 'What is the equation of the perpendicular bisector of the segment from A(2, 4) to B(8, 4)?',
    options: ['x = 5', 'y = 4', 'x = 4', 'y = 5'],
    correctAnswer: 0,
    solution: [
      'Midpoint of AB = ((2+8)/2, (4+4)/2) = (5, 4)',
      'AB is horizontal (slope = 0), so the perpendicular bisector is vertical.',
      'A vertical line through (5, 4) has equation x = 5.',
      'Answer: x = 5'
    ]
  }
];
